import { IOptionAnyValue } from 'bricks/types';
import { IPropertyType } from '../../types';
import { EXPENSE_CALCULATION_MODE } from './constants';

export interface BOESlideoutFormValues {
    id: string | null;
    dealId: number | null;
    name: string;
    propertyType: IOptionAnyValue;
    selectedLoanCalculationOptions: any;
    grossSquareFootage: number | null;
    efficiency: number | null;
    rentPrice: number | null;
    units: number | null;
    expenses: number | null;
    exitCapRate: number | null;
    acquisitionCosts: number | null;
    hardCosts: number | null;
    softCosts: number | null;
    developerFee: number | null;
    apr: number | null;
    avgLoanBalance: number | null;
    loanTerm: number | null;
    contructionFinancing: number | null;
    marketCapRate: number | null;
    developerFeeTotal: number | null;
    interestReserveTotal: number | null;
    totalDevelopmentCosts: number | null;
    loanAmount: number | null;
    equityCost: number | null;
    rentPriceSubdiv: 'unit' | 'sf' | 'kw' | 'mw';
    expenseCostSubdiv: 'unit' | 'sf' | 'kw' | 'mw';
    expenseCalculationMode: EXPENSE_CALCULATION_MODE;
    expensePercentOfGpr: number | null;
    softCostsSubdiv: 'unit' | 'sf' | 'kw' | 'mw' | 'total';
    hardCostsSubdiv: 'unit' | 'sf' | 'kw' | 'mw' | 'total';
    rentPriceTime: 'monthly' | 'yearly';
    expenseCostTime: 'monthly' | 'yearly';
    loanTermTime: 'monthly' | 'yearly';
    isNewScenario?: boolean;
    createNewDeal?: boolean;
    // Retail-specific fields for Apartment with Retail
    retailGrossSquareFootage?: number | null;
    retailRentPrice?: number | null;
    retailExpensePercentOfGpr?: number | null;
    retailExpenseCostTime?: 'monthly' | 'yearly';
    retailExpenseCostSubdiv?: 'sf';
    retailExpenses?: number | null;
    retailExpenseCalculationMode?: EXPENSE_CALCULATION_MODE;
    retailRentPriceTime?: 'monthly' | 'yearly';
    retailNumberOfSuites?: number | null;
    retailRentPricePerSf?: number | null;
    retailExpenseCostPerSf?: number | null;
}

export const BOE_FIELD: { [K in keyof BOESlideoutFormValues]: K } = {
    id: 'id',
    dealId: 'dealId',
    name: 'name',
    propertyType: 'propertyType',
    selectedLoanCalculationOptions: 'selectedLoanCalculationOptions',
    grossSquareFootage: 'grossSquareFootage',
    efficiency: 'efficiency',
    rentPrice: 'rentPrice',
    units: 'units',
    expenses: 'expenses',
    retailExpenses: 'retailExpenses',
    exitCapRate: 'exitCapRate',
    acquisitionCosts: 'acquisitionCosts',
    hardCosts: 'hardCosts',
    softCosts: 'softCosts',
    developerFee: 'developerFee',
    apr: 'apr',
    avgLoanBalance: 'avgLoanBalance',
    loanTerm: 'loanTerm',
    contructionFinancing: 'contructionFinancing',
    marketCapRate: 'marketCapRate',
    developerFeeTotal: 'developerFeeTotal',
    interestReserveTotal: 'interestReserveTotal',
    totalDevelopmentCosts: 'totalDevelopmentCosts',
    loanAmount: 'loanAmount',
    equityCost: 'equityCost',
    rentPriceSubdiv: 'rentPriceSubdiv',
    expenseCostSubdiv: 'expenseCostSubdiv',
    expenseCalculationMode: 'expenseCalculationMode',
    expensePercentOfGpr: 'expensePercentOfGpr',
    softCostsSubdiv: 'softCostsSubdiv',
    hardCostsSubdiv: 'hardCostsSubdiv',
    rentPriceTime: 'rentPriceTime',
    expenseCostTime: 'expenseCostTime',
    loanTermTime: 'loanTermTime',
    isNewScenario: 'isNewScenario',
    createNewDeal: 'createNewDeal',
    retailGrossSquareFootage: 'retailGrossSquareFootage',
    retailExpensePercentOfGpr: 'retailExpensePercentOfGpr',
    retailExpenseCostTime: 'retailExpenseCostTime',
    retailExpenseCostSubdiv: 'retailExpenseCostSubdiv',
    retailExpenseCalculationMode: 'retailExpenseCalculationMode',
    retailRentPrice: 'retailRentPrice',
    retailRentPriceTime: 'retailRentPriceTime',
    retailNumberOfSuites: 'retailNumberOfSuites',
    retailRentPricePerSf: 'retailRentPricePerSf',
    retailExpenseCostPerSf: 'retailExpenseCostPerSf',
};

export interface IBackOfEnvelopePayload {
    dealId: number | null;
    name: string;
    builtSf: number;
    buildingEfficiency?: number;
    monthlyRentPricePerUnit?: number;
    monthlyRentPricePerSf?: number;
    rentPriceTime: 'monthly' | 'yearly';
    rentPriceSubdiv: 'unit' | 'sf' | 'kw' | 'mw';
    numberOfUnits: number;
    monthlyExpenseCostPerUnit?: number;
    monthlyExpenseCostPerSf?: number;
    expenseCostTime: 'monthly' | 'yearly';
    expenseCostSubdiv: 'unit' | 'sf' | 'kw' | 'mw';
    expenseCalculationMode: EXPENSE_CALCULATION_MODE;
    expensePercentOfGpr?: number;
    assumedExitCapRate?: number;
    acquisition: number;
    hardCosts?: number;
    hardCostsSubdiv: 'unit' | 'sf' | 'kw' | 'mw' | 'total';
    softCosts?: number;
    softCostsSubdiv: 'unit' | 'sf' | 'kw' | 'mw' | 'total';
    developerFeeRate?: number;
    annualPercentageRate?: number;
    averageLoanBalance?: number;
    loanTermMonths: number;
    loanTermTime: 'monthly' | 'yearly';
    propertyTypeId: number;
    propertyType?: IPropertyType;
    loanToCost?: number;
    loanToValue?: number;
    debtYield?: number;
    id?: number;
    marketCapRate?: number;
    retailBuiltSf?: number;
    retailExpenseCostPerSf?: number;
    retailRentPricePerSf?: number;
    retailExpenseCostSubdiv?: 'sf';
    retailNumberOfSuites?: number;
    retailRentPriceTime?: 'monthly' | 'yearly';
    retailExpenseCalculationMode?: EXPENSE_CALCULATION_MODE;
    retailExpenseCostTime?: 'monthly' | 'yearly';
    retailExpensePercentOfGpr?: number;
}

export interface IVariables {
    postData: IBackOfEnvelopePayload;
    orgId: string;
}
